import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '@/components/admin/AdminHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { ArrowLeft, Save } from 'lucide-react';

const RedirectsNew = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    from_path: '',
    to_path: '',
    redirect_type: '301',
    is_active: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.from_path.trim()) {
      toast.error('Please enter the source path');
      return;
    }

    if (!formData.to_path.trim()) {
      toast.error('Please enter the destination path');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('redirects')
        .insert({
          from_path: formData.from_path.trim(),
          to_path: formData.to_path.trim(),
          redirect_type: parseInt(formData.redirect_type),
          is_active: formData.is_active,
          created_by: user?.id,
        });

      if (error) throw error;

      toast.success('Redirect created successfully');
      navigate('/admin/redirects');
    } catch (error: any) {
      console.error('Error creating redirect:', error);
      if (error.code === '23505') {
        toast.error('A redirect from this path already exists');
      } else {
        toast.error('Failed to create redirect');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <AdminHeader 
        title="Add Redirect" 
        description="Create a new URL redirect"
      />
      
      <main className="p-6">
        <Button 
          variant="ghost" 
          className="mb-4"
          onClick={() => navigate('/admin/redirects')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Redirects
        </Button>

        <form onSubmit={handleSubmit}>
          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle>Redirect Details</CardTitle>
              <CardDescription>Configure where old URLs should redirect to</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="from_path">From Path *</Label>
                <Input
                  id="from_path"
                  value={formData.from_path}
                  onChange={(e) => setFormData(prev => ({ ...prev, from_path: e.target.value }))}
                  placeholder="/old-page-url"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  The old URL path that should be redirected (e.g., /old-blog-post)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="to_path">To Path *</Label>
                <Input
                  id="to_path"
                  value={formData.to_path}
                  onChange={(e) => setFormData(prev => ({ ...prev, to_path: e.target.value }))}
                  placeholder="/new-page-url"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  The new URL path to redirect to (e.g., /new-blog-post)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="redirect_type">Redirect Type</Label>
                <Select
                  value={formData.redirect_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, redirect_type: value }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select redirect type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="301">301 - Permanent Redirect</SelectItem>
                    <SelectItem value="302">302 - Temporary Redirect</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Use 301 for permanent moves, 302 for temporary redirects
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="is_active">Active</Label>
                  <p className="text-xs text-muted-foreground">
                    Enable or disable this redirect
                  </p>
                </div>
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" disabled={isLoading}>
                  <Save className="h-4 w-4 mr-2" />
                  {isLoading ? 'Creating...' : 'Create Redirect'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate('/admin/redirects')}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </main>
    </div>
  );
};

export default RedirectsNew;
